import React, { useEffect } from 'react';
import ProductCard from '../components/ProductCard';
import Loader from '../components/Loader';
import { useNavigate } from 'react-router-dom';
import { Pagination, Stack } from '@mui/material';
import useProductListing from '../hooks/useProductListing';

// NUTRACEUTICALS. The original filter was
// ['vitamins-and-supplements', 'nutrition'], and `nutrition` **does not exist**
// in the catalogue — that clause never matched anything. The nearest real
// category is `sports-nutrition` (id 80), which does exist but holds 0
// products, so it is almost certainly what was meant and would have rendered
// nothing regardless.
//
// The store owner's decision was that this page is "Nutraceuticals", the usual
// Indian retail-pharmacy umbrella for supplements plus health foods and drinks.
// There is no `nutraceuticals` category in WooCommerce (checked against all 18
// categories, the tags, and product names), so the label is the page's and the
// contents are the two real stocked categories it covers:
//
//   vitamins-and-supplements  (id 73)  115 products
//   health-food-and-drinks    (id 78)   70 products
//                                      --- verified live: 73,78 -> 185, no overlap
//
// `sports-nutrition` is deliberately NOT listed: an empty category contributes
// nothing but would make this page's slug list look like it covers more than it
// does. Add it here once products are assigned to it.
//
// If a real `nutraceuticals` category is ever created in WooCommerce and the
// products re-assigned to it, this list becomes that one slug and nothing else
// here changes.
const CATEGORY_SLUGS = ['vitamins-and-supplements', 'health-food-and-drinks'];

function MultivitaminsSupplements({ isAuthenticated, onAddToCart }) {
  const navigate = useNavigate();

  const {
    products,
    loading,
    error,
    missingCategories,
    currentPage,
    totalPages,
    setPage,
  } = useProductListing({ categorySlugs: CATEGORY_SLUGS, perPage: 30 });

  // Both slugs resolve today, so an unmatched one means a category was renamed
  // or deleted in WooCommerce. Log it: a half-stocked page looks like normal
  // stock levels, which is how the `nutrition` clause survived unnoticed.
  useEffect(() => {
    if (missingCategories.length > 0) {
      console.warn('Nutraceuticals: unknown category slug(s)', missingCategories);
    }
  }, [missingCategories]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user_data')) || {};
    if (userData.isSuperAdmin) navigate('/dashboard');
  }, [navigate]);

  if (loading) return <Loader />;

  return (
    <div className="content-wrapper">
      <section className="multivetamins">
        <h3 style={{ fontSize: '22px', textAlign: 'center', margin: '3% 0' }}>
          {'Nutraceuticals'.toLocaleUpperCase()}
        </h3>
        <section className="char-products-section" style={{ margin: '0 0 20px' }}>
          <div className="small-container d-grid">
            {products.length > 0 ? (
              products.map(item => (
                <ProductCard
                  isAuthenticated={isAuthenticated}
                  onAddToCart={onAddToCart}
                  key={item.id}
                  product={item}
                />
              ))
            ) : (
              <p>
                {error
                  ? error
                  : missingCategories.length === CATEGORY_SLUGS.length
                    // Every slug is unknown, so this is a misconfiguration, not
                    // an empty shelf. Say which it is.
                    ? 'This section is not set up yet. Please check back soon.'
                    : 'No products found!'}
              </p>
            )}
          </div>
          {totalPages > 1 && (
            <Stack spacing={2} style={{ margin: '5% auto 0', display: 'flex', alignItems: 'center' }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(event, value) => setPage(value)}
                color="primary"
              />
            </Stack>
          )}
        </section>
      </section>
    </div>
  );
}

export default MultivitaminsSupplements;
